import React, { useEffect, useState } from "react";
import TwoFASetup from "./TwoFaSetup";
import TwoFAVerify from "./TwoFAVerify";
import {
  setup2FAReq,
  twoFAStatusReq,
  enable2FAReq,
  disable2FAReq,
} from "../../../utils/apis/2fa";
import { useSelector } from "react-redux";

// Theme tokens — replace with your brand colors when provided
const theme = {
  bg: "#131313", // surface-0
  panel: "#030504", // surface-1
  text: "#e6eaf2", // text
  muted: "#b0b0b0", // text-muted
  primary: "#4bb390", // primary (to replace)
  accent: "#22d3ee", // accent (to replace)
  danger: "#ef4444", // status disabled (optional override)
  radius: 16,
  border: "1px solid var(--border-color)",
};

const wrap = (styles) => Object.assign({ boxSizing: "border-box" }, styles);

const Card = ({ children, style }) => (
  <section
    style={wrap({
      background: theme.bg,
      border: "1px solid rgba(255,255,255,.06)",
      borderRadius: 24,
      padding: 32,
      boxShadow: "0 8px 28px rgba(0,0,0,.35), 0 2px 6px rgba(0,0,0,.2)",
      ...style,
    })}
  >
    {children}
  </section>
);

const StatusBadge = ({ enabled }) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 12px",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: ".3px",
    fontSize: 12,
    border: "1px solid transparent",
  };
  return enabled ? (
    <span
      style={wrap({
        ...base,
        background: "rgba(34,197,94,.12)",
        color: "#bbf7d0",
        borderColor: "rgba(34,197,94,.35)",
      })}
    >
      ENABLED
    </span>
  ) : (
    <span
      style={wrap({
        ...base,
        background: "rgba(239,68,68,.12)",
        color: "#fecaca",
        borderColor: "#ff4343",
      })}
    >
      DISABLED
    </span>
  );
};

const PrimaryButton = ({ children, ...props }) => (
  <button
    {...props}
    style={wrap({
      background: theme.primary,
      color: "#fff",
      border: "none",
      borderRadius: 12,
      padding: "12px 18px",
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: "0 6px 16px rgba(0,0,0,.3)",
    })}
    onMouseOver={(e) => (e.currentTarget.style.filter = "brightness(1.05)")}
    onMouseOut={(e) => (e.currentTarget.style.filter = "none")}
  >
    {children}
  </button>
);

const StepCard = ({ num, title, desc }) => (
  <div
    style={wrap({
      background: theme.panel,
      border: theme.border,
      borderRadius: 16,
      padding: 18,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    })}
  >
    <div
      style={wrap({
        width: 28,
        height: 28,
        borderRadius: 999,
        background: `linear-gradient(135deg, ${theme.accent}, ${theme.primary})`,
        display: "grid",
        placeItems: "center",
        fontWeight: 700,
        color: "#0b1220",
      })}
    >
      {num}
    </div>
    <h3 style={wrap({ margin: "6px 0 0", fontSize: 16 })}>{title}</h3>
    <p style={wrap({ margin: 0, color: theme.muted, fontSize: 14 })}>{desc}</p>
  </div>
);

const Tips = () => (
  <div
    style={wrap({
      marginTop: 18,
      padding: 18,
      borderRadius: 16,
      background: theme.panel,
      border: theme.border,
    })}
  >
    <h4 style={wrap({ margin: "0 0 10px" })}>Security Tips</h4>
    <ul style={wrap({ margin: 0, paddingLeft: 18, color: theme.muted })}>
      <li>Keep the authenticator app secure and private.</li>
      <li>Use trusted apps like Google Authenticator or Authy.</li>
      <li>Back up recovery codes when available.</li>
      <li>Disable 2FA only if absolutely necessary.</li>
    </ul>
  </div>
);

export default function TwoFASettings() {
  const [twoFAstatus, setTwoFAstatus] = useState({});
  const [setupData, setSetupData] = useState(null);
  const [currentStep, setCurrentStep] = useState("main"); // 'main', 'setup', 'verify'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { idToken, email } = useSelector((state) => state.auth);

  const fetch2FAstatus = async () => {
    try {
      const response = await twoFAStatusReq(idToken);
      console.log(response, "2FA status response");
      setTwoFAstatus(response);
    } catch (error) {
      console.error("Error fetching 2FA status:", error);
      setError("Failed to fetch 2FA status");
    }
  };

  const handleSetup2FAReq = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!email) {
        setError("User email not found. Please try logging in again.");
        return;
      }

      // Note: setup2FAReq expects email and password data
      // For now, we'll use the email from auth state
      // You might need to handle password differently or modify the API
      const userData = {
        email: email,
        password: "password", // This might need to be handled differently
      };

      const response = await setup2FAReq(userData, idToken);
      console.log(response, "Setup 2FA response");

      if (response.success) {
        setSetupData(response);
        setCurrentStep("setup");
      } else {
        setError("Failed to setup 2FA. Please try again.");
      }
    } catch (error) {
      console.error("Error setting up 2FA:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to setup 2FA. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToVerify = () => {
    setCurrentStep("verify");
  };

  const handleBackToSetup = () => {
    setCurrentStep("setup");
  };

  const handleVerifyCode = async (code) => {
    setLoading(true);
    setError(null);
    try {
      console.log("Verifying code:", code);
      console.log("Using temp_secret:", setupData?.temp_secret);

      const response = await enable2FAReq(
        {
          otp_code: code,
          temp_secret: setupData?.temp_secret,
        },
        idToken
      );

      console.log(response, "Enable 2FA response");

      // Refresh status and go back to main view
      await fetch2FAstatus();
      setCurrentStep("main");
      setSetupData(null);
      setSuccess("Two-factor authentication has been enabled successfully!");
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error("Error enabling 2FA:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Invalid code. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await disable2FAReq(idToken);
      console.log(response, "Disable 2FA response");

      // Refresh status and show success message
      await fetch2FAstatus();
      setSuccess("Two-factor authentication has been disabled successfully!");
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error("Error disabling 2FA:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to disable 2FA. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMain = () => {
    setCurrentStep("main");
    setSetupData(null);
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    fetch2FAstatus();
  }, []);

  // Show setup QR section
  if (currentStep === "setup" && setupData) {
    return (
      <TwoFASetup
        otpauth={setupData.totp_uri}
        secret={setupData.secret}
        message={setupData.message}
        onContinue={handleContinueToVerify}
        onBack={handleBackToMain}
      />
    );
  }

  // Show verification section
  if (currentStep === "verify") {
    return (
      <TwoFAVerify
        onBack={handleBackToSetup}
        onSubmit={handleVerifyCode}
        loading={loading}
        error={error}
      />
    );
  }

  // Main view
  return (
    <div
      style={wrap({
        margin: 0,
        color: theme.text,
        fontFamily: "Inter, system-ui, sans-serif",
      })}
    >
      <div>
        <Card>
          <h1 style={wrap({ fontSize: 28, margin: "0 0 6px" })}>
            Two‑Factor Authentication
          </h1>
          <p style={wrap({ color: theme.muted, margin: "0 0 28px" })}>
            Add an extra layer of security to the account.
          </p>

          {error && (
            <div
              style={wrap({
                background: "rgba(239,68,68,.12)",
                color: "#fecaca",
                border: "1px solid rgba(239,68,68,.35)",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 24,
              })}
            >
              {error}
            </div>
          )}

          {success && (
            <div
              style={wrap({
                background: "rgba(34,197,94,.12)",
                color: "#bbf7d0",
                border: "1px solid rgba(34,197,94,.35)",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 24,
              })}
            >
              {success}
            </div>
          )}

          <div
            style={wrap({
              display: "flex",
              gap: 18,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 24,
              flexWrap: "wrap",
              fontFamily: "Inter,sans-serif",
            })}
          >
            <div
              style={wrap({ display: "flex", gap: 12, alignItems: "center" })}
            >
              {twoFAstatus?.is_2fa_enabled ? (
                <button
                  onClick={handleDisable2FA}
                  disabled={loading}
                  style={wrap({
                    background: theme.danger,
                    color: "#fff",
                    border: "none",
                    borderRadius: 12,
                    padding: "12px 18px",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1,
                    boxShadow: "0 6px 16px rgba(0,0,0,.3)",
                  })}
                  onMouseOver={(e) =>
                    !loading &&
                    (e.currentTarget.style.filter = "brightness(1.05)")
                  }
                  onMouseOut={(e) =>
                    !loading && (e.currentTarget.style.filter = "none")
                  }
                >
                  {loading ? "Disabling..." : "Disable 2FA"}
                </button>
              ) : (
                <PrimaryButton
                  onClick={handleSetup2FAReq}
                  disabled={loading}
                  style={loading ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                >
                  {loading ? "Setting up..." : "Enable 2FA"}
                </PrimaryButton>
              )}
            </div>
            <StatusBadge enabled={twoFAstatus?.is_2fa_enabled} />
          </div>

          <h2 style={wrap({ fontSize: 18, margin: "18px 0 8px" })}>
            How it works
          </h2>

          <div
            style={wrap({
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 16,
            })}
          >
            <StepCard
              num="1"
              title="Scan QR Code"
              desc="Open an authenticator app and scan the provided QR code."
            />
            <StepCard
              num="2"
              title="Enter Code"
              desc="Type the 6‑digit code from the app to verify."
            />
            <StepCard
              num="3"
              title="Secure Login"
              desc="Use the code during sign‑in for stronger protection."
            />
          </div>

          <Tips />
        </Card>
      </div>
    </div>
  );
}
